/**
 * Mi negocio — la pestaña de entrada de Mi Negocio.
 *
 * Antes esto era «Resumen»: una ficha de solo lectura que repetía los datos
 * del negocio, y para cambiar cualquiera de ellos había que irse a
 * Configuración → Perfil, otro menú. Dos pantallas para el mismo dato, una
 * mirando y otra editando.
 *
 * Ahora vive aquí completo y editable. Configuración desapareció como menú:
 * todo lo que tenía se reubicó donde ya se estaba trabajando.
 *
 * Lo que NO está aquí a propósito: el recuadro de «pon en marcha la atención
 * automática». Era una copia del menú Atención automática -mismo estado, mismo
 * botón, misma información- y tener el mismo panel en dos sitios hace que el
 * dueño no sepa cuál es el bueno.
 */
import { PantallaCargando } from '@/components/Cargando';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import PhoneInput from '@/components/PhoneInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import MarcaDelNegocio from '@/components/branding/MarcaDelNegocio';
import { Sparkles, ArrowRight, Building2, Save, User } from 'lucide-react';

const ZONAS = [
  'America/Mexico_City', 'America/Monterrey', 'America/Cancun', 'America/Tijuana',
  'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Buenos_Aires',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'Europe/Madrid', 'UTC',
];

export default function BusinessResumen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [contactRules, setContactRules] = useState({});
  const [verticalConfig, setVerticalConfig] = useState({});
  const [verticalFields, setVerticalFields] = useState([]);
  const [subtipos, setSubtipos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    api.get('/tenant/profile').then(({ data }) => {
      setProfile(data);
      setForm({
        name: data.name || '', phone: data.phone || '', email: data.email || '',
        website: data.website || '', tax_id: data.tax_id || '',
        address: data.address || '', description: data.description || '',
        timezone: data.timezone || 'America/Mexico_City',
        locale: data.locale || 'es-MX', currency: data.currency || 'MXN',
        date_format: data.date_format || 'DD/MM/YYYY',
        time_format: data.time_format || '24h',
        phone_code: data.phone_code || '+52',
        reminder_lead_hours: data.reminder_lead_hours || 24,
        subtype: data.subtype || '',
      });
      setContactRules(data.contact_rules || {});
      setVerticalConfig(data.vertical_config || {});
      setVerticalFields(data.vertical_fields?.suggested_fields || []);
      api.get('/verticals').then(vr => {
        const igual = (vr.data || []).find(v => v.key === data.vertical);
        setSubtipos(igual?.subtypes || []);
      }).catch(() => {});
    }).catch(() => {}).finally(() => setCargando(false));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true); setAviso(''); setError('');
    try {
      await api.put('/tenant/profile', {
        ...form, vertical_config: verticalConfig, contact_rules: contactRules,
      });
      setAviso('Datos guardados');
      setTimeout(() => setAviso(''), 3000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setGuardando(false); }
  };

  // Los campos que cambian según la vertical del negocio (taller, hotel,
  // restaurante...). Se dibujan solos desde lo que declara el backend.
  const campoVertical = (campo) => {
    const val = verticalConfig[campo.key]
      ?? (campo.type === 'boolean' ? false : campo.type === 'multi_select' ? [] : '');
    const set = (v) => setVerticalConfig({ ...verticalConfig, [campo.key]: v });

    if (campo.type === 'boolean') {
      return (
        <div key={campo.key} className="flex items-center justify-between py-2">
          <Label className="text-sm text-[#0F172A]">{campo.label}</Label>
          <Switch checked={!!val} onCheckedChange={set} data-testid={`vc-${campo.key}`} />
        </div>
      );
    }
    if (campo.type === 'multi_select') {
      const elegidos = Array.isArray(val) ? val : [];
      return (
        <div key={campo.key} className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{campo.label}</Label>
          <div className="flex flex-wrap gap-2">
            {campo.options?.map(o => (
              <button key={o} type="button"
                onClick={() => set(elegidos.includes(o) ? elegidos.filter(x => x !== o) : [...elegidos, o])}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${elegidos.includes(o)
                  ? 'bg-[#F72585] text-white border-[#F72585]'
                  : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (campo.type === 'select') {
      return (
        <div key={campo.key} className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{campo.label}</Label>
          <Select value={val || ''} onValueChange={set}>
            <SelectTrigger className="bg-white border-[#E2E8F0] text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
            <SelectContent>{campo.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div key={campo.key} className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{campo.label}</Label>
        <Input value={val || ''} type={campo.type === 'number' ? 'number' : 'text'}
          onChange={e => set(campo.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
          className="bg-white border-[#E2E8F0] text-sm" />
      </div>
    );
  };

  if (cargando) return <PantallaCargando />;

  return (
    <div data-testid="business-resumen" className="space-y-5">
      <SectionHelp module="business_resumen" />

      {profile && !profile.onboarding_completed && (
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-4 sm:p-6 text-white" data-testid="onboarding-prompt">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#F72585]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-[#F72585]" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold tracking-tight">Configura tu negocio</h3>
                <p className="text-xs sm:text-sm text-slate-300">Completa el asistente para dejar todo listo</p>
              </div>
            </div>
            <Button onClick={() => navigate('/tenant/onboarding')} className="bg-[#F72585] text-white hover:bg-[#D81B60] w-full sm:w-auto" data-testid="start-onboarding-btn">
              Comenzar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-[#F72585]" />
            <h3 className="text-lg font-semibold text-[#0F172A]">Datos de tu negocio</h3>
          </div>
          <Button onClick={guardar} disabled={guardando} className="bg-[#F72585] text-white hover:bg-[#D81B60] w-full sm:w-auto" data-testid="save-profile-btn">
            <Save className="w-4 h-4 mr-2" /> {guardando ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
        {aviso && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">{aviso}</div>}

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre del negocio</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Teléfono</Label>
              <PhoneInput value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-phone" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email del negocio</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Sitio web</Label>
              <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-website" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">RFC / ID Fiscal</Label>
              <Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-tax-id" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Zona horaria</Label>
              <Select value={form.timezone} onValueChange={v => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>{ZONAS.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Dirección</Label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-white border-[#E2E8F0] text-sm" data-testid="profile-address" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Descripción</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-[#E2E8F0] text-sm min-h-[70px]" data-testid="profile-description" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Antelación de recordatorios (horas)</Label>
            <Input type="number" min="1" value={form.reminder_lead_hours}
              onChange={e => setForm({ ...form, reminder_lead_hours: e.target.value ? parseInt(e.target.value, 10) : '' })}
              className="bg-white border-[#E2E8F0] text-sm max-w-[160px]" data-testid="profile-reminder-lead-hours" />
            <p className="text-xs text-[#64748B]">
              Con cuántas horas de anticipación se avisa antes de una cita, y con cuántas se avisa a un vendedor
              si un lead nuevo lleva sin actividad. Las tareas del CRM avisan la mañana de su fecha. Por defecto, 24 horas.
            </p>
          </div>

          <div className="border-t border-[#E2E8F0] pt-6">
            <h4 className="text-sm font-semibold text-[#0F172A] mb-1">Calificación de leads por mensaje directo</h4>
            <p className="text-xs text-[#64748B] mb-4">
              Cuando un cliente escribe por Messenger o Instagram sin dar su nombre, tu asistente de IA ya pregunta
              «¿Con quién tengo el gusto?» una sola vez por conversación.
            </p>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm text-[#0F172A]">Pedir WhatsApp si falta</Label>
                <p className="text-[11px] text-[#94A3B8]">
                  Si el cliente no ha dado un teléfono, tu asistente le pide un número una sola vez por
                  conversación (nunca en comentarios públicos).
                </p>
              </div>
              <Switch
                checked={!!contactRules.lead_qualification_fields?.phone}
                onCheckedChange={(v) => setContactRules({
                  ...contactRules,
                  lead_qualification_fields: { ...(contactRules.lead_qualification_fields || {}), phone: v },
                })}
                data-testid="qualification-require-phone"
              />
            </div>
          </div>

          {subtipos.length > 0 && (
            <div className="border-t border-[#E2E8F0] pt-6">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Sub-vertical (opcional)</Label>
              <select value={form.subtype || ''} onChange={e => setForm({ ...form, subtype: e.target.value })}
                className="w-full mt-2 h-11 rounded-lg border border-[#E2E8F0] bg-white text-sm px-3"
                data-testid="settings-subtype-select">
                <option value="">Sin especificar</option>
                {subtipos.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          {verticalFields.length > 0 && (
            <div className="border-t border-[#E2E8F0] pt-6">
              <h4 className="text-sm font-semibold text-[#0F172A] mb-4">
                Configuración de tu giro: <span className="text-[#F72585] capitalize">{profile?.vertical?.replace(/_/g, ' ')}</span>
              </h4>
              <div className="space-y-4">{verticalFields.map(campoVertical)}</div>
            </div>
          )}
        </div>
      </div>

      {/* La marca del negocio. Va después de la ficha y antes del bloque del
          usuario porque es del negocio, no de la persona. */}
      <MarcaDelNegocio />

      {/* Quién eres tú dentro de este negocio. Se queda de solo lectura: el
          nombre y el correo del usuario se cambian en su propia cuenta, no
          desde la ficha del negocio. */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <User className="w-5 h-5" /> Tu usuario
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Dato label="Nombre" valor={user?.name} />
          <Dato label="Correo" valor={user?.email} />
          <Dato label="Rol" valor={user?.tenant_role?.replace(/_/g, ' ')} capitalizar />
          <Dato label="Zona horaria" valor={user?.timezone} />
        </div>
      </div>
    </div>
  );
}

function Dato({ label, valor, capitalizar }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{label}</p>
      <p className={`text-sm text-[#0F172A] ${capitalizar ? 'capitalize' : ''}`}>{valor || '—'}</p>
    </div>
  );
}
